import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {environment} from '../../environments';
import {reportTicketError, reportTicketMessage} from '../helpers';
import {Service} from '../models';
import {AuthenticationService} from './authentication.service';


const SURGE_ZERO_TOUCH_SOURCE = 'surge_zero_touch';

@Injectable({
    providedIn: 'root'
})
export class LuxEngineService {

    private _projectIdsByServiceId: { [serviceId: string]: string } = {};

    constructor(private http: HttpClient, private authService: AuthenticationService) {
    }


    // Resolves true only when a projectId was created and stored for the service.
    async createZeroTouchProject(serviceData: Service, uploadTimestamp: number): Promise<boolean> {
        const member = this.authService.memberCurrentData;
        const body = {
            metadata: {
                service_id: serviceData.id,
                address: serviceData.address,
                member_id: this.getMemberUid(),
                member_email: member?.loginEmail,
                user_type: 'bwr',
                brief: 'ZERO_TOUCH',
                order_details: '',
                source: SURGE_ZERO_TOUCH_SOURCE,
                upload_timestamp: uploadTimestamp,
            },
        };

        try {
            const response = await this.http.post<{ data?: { projectId?: string } }>(`${environment.apiBaseUrl}/member-luxengine-create-project`, body).toPromise();
            const projectId = response?.data?.projectId;
            if (!projectId || !serviceData.id) {
                console.error('[DEBUG createZeroTouchProject] member-luxengine-create-project responded without a projectId for serviceId:', serviceData.id);
                reportTicketMessage('createZeroTouchProject', serviceData, 'warning', 'member-luxengine-create-project responded without a projectId', {
                    existingTicketId: serviceData.ticketId,
                });
                return false;
            }
            this._projectIdsByServiceId[serviceData.id] = projectId;
            console.debug('[DEBUG createZeroTouchProject] success for serviceId:', serviceData.id, '| projectId:', projectId);
            reportTicketMessage('createZeroTouchProject', serviceData, 'info', 'member-luxengine-create-project succeeded', {
                existingTicketId: serviceData.ticketId,
            });
            return true;
        } catch (err) {
            console.error('[DEBUG createZeroTouchProject] FAILED for serviceId:', serviceData.id, err);
            reportTicketError('createZeroTouchProject', serviceData, err, {
                existingTicketId: serviceData.ticketId,
            });
            return false;
        }
    }

    // Resolves true only when member-luxengine-process-assets succeeded.
    async processAssets(serviceData: Service, uploadTimestamp: number): Promise<boolean> {
        const projectId = serviceData.id && this._projectIdsByServiceId[serviceData.id];
        if (!projectId) {
            console.error('[DEBUG processAssets] No LuxEngine projectId found for serviceId:', serviceData.id);
            reportTicketMessage('processAssets', serviceData, 'warning', 'member-luxengine-process-assets skipped: no projectId on file for this service', {
                existingTicketId: serviceData.ticketId,
            });
            return false;
        }

        try {
            await this.http.post(`${environment.apiBaseUrl}/member-luxengine-process-assets`, {projectId, uploadTimestamp}).toPromise();
            console.debug('[DEBUG processAssets] success for serviceId:', serviceData.id, '| projectId:', projectId);
            reportTicketMessage('processAssets', serviceData, 'info', 'member-luxengine-process-assets succeeded', {
                existingTicketId: serviceData.ticketId,
            });
            return true;
        } catch (err) {
            console.error('[DEBUG processAssets] FAILED for serviceId:', serviceData.id, err);
            reportTicketError('processAssets', serviceData, err, {
                existingTicketId: serviceData.ticketId,
            });
            return false;
        }
    }

    private getMemberUid() {
        return this.authService.memberCurrentData?.isContractor
            ? this.authService.memberCurrentData?.mainMemberUid
            : this.authService.memberCurrentData?.memberProfileUid;
    }
}
